import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import ProgressBar from "@ramonak/react-progress-bar";
import { FaUsers, FaUser, FaChevronDown, FaChevronUp, FaSearch, FaTimes } from "react-icons/fa";
import SkeletonLoader from "../Loaders/SkeletonLoader";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { Calendar } from "react-feather";
import CustomNode from "./CustomNode";
import openNotification from "components/utils/CustomNotification";
import qs from "qs";
import ReactFlow, {
  Controls,
  Background,
  Position,
  Edge,
  Node,
  ReactFlowProvider,
  Handle,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import "./HierarchyComponent.css";
import Loader from "components/Loaders/Loader";
import moment from "moment";
import NodeDetailsModal from "./NodeDetailsModal";
import { Result } from "antd";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";

const API_URL_HIERARCHY = `${process.env.REACT_APP_API_URL}/api/get-hierarchy-metrics`;

interface TreeNode {
  job_title: string;
  user_uid: string;
  name: string;
  department: string;
  self_metrics: any;
  vertical_metrics: any;
  self_metrics_score: number;
  vertical_metrics_score: number;
  overall_performance: number;
  children: TreeNode[];
  horizontal_metrics?: any[];
  location?: string;
}

interface SearchResult {
  user_uid: string;
  name: string;
  job_title: string;
  department: string;
  type: 'reportee' | 'peer';
}

const extractTreeData = (data: any): TreeNode => {
  return {
    job_title: data.job_title,
    user_uid: data.user_uid,
    name: data.name,
    department: data.department,
    self_metrics: data.self_metrics ?? null,
    vertical_metrics: data.vertical_metrics ?? null,
    overall_performance: data.self_metrics?.data?.[0]?.overall_performance ?? 0,
    children: data.children ? data.children.map(extractTreeData) : [],
    self_metrics_score: data.self_metrics?.data?.[0]?.overall_performance ?? 0,
    vertical_metrics_score: data.vertical_metrics?.data?.[0]?.overall_performance ?? 0,
    horizontal_metrics: data.horizontal_metrics ?? [],
    location: data.location ?? "",
  };
};

// Helper function to find the path from root to a specific node
const findPathToNode = (nodeId: string, node: TreeNode, path: string[] = []): string[] | null => {
  const currentPath = [...path, node.user_uid];

  if (node.user_uid === nodeId) {
    return currentPath;
  }

  for (const child of node.children) {
    const result = findPathToNode(nodeId, child, currentPath);
    if (result) return result;
  }

  return null;
};

// Helper function to find the parent of a node
const findParentNode = (nodeId: string, node: TreeNode): TreeNode | null => {
  for (const child of node.children) {
    if (child.user_uid === nodeId) {
      return node;
    }
    const result = findParentNode(nodeId, child);
    if (result) return result;
  }
  return null;
};

// Helper function to collect all nodes from tree (for search)
const collectAllNodes = (node: TreeNode): TreeNode[] => {
  const nodes: TreeNode[] = [node];
  node.children.forEach(child => {
    nodes.push(...collectAllNodes(child));
  });
  return nodes;
};

const generateGraph = (
  rootNode: TreeNode,
  expandedNodes: Set<string>,
  toggleNode: (nodeId: string) => void,
  x = 0,
  y = 0
): { nodes: Node[]; edges: Edge[] } => {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Responsive configuration based on screen size
  const getResponsiveConfig = () => {
    const screenWidth = window.innerWidth;

    if (screenWidth <= 768) {
      return {
        maxNodesPerRow: 2,
        horizontalSpacing: 400,
        verticalSpacing: 215
      };
    } else if (screenWidth <= 1024) {
      return {
        maxNodesPerRow: 3,
        horizontalSpacing: 400,
        verticalSpacing: 215
      };
    } else {
      return {
        maxNodesPerRow: 5,
        horizontalSpacing: 400,
        verticalSpacing: 215
      };
    }
  };

  // Find which nodes should be visible based on expansion state
  const getVisibleNodes = (node: TreeNode, currentPath: string[] = []): TreeNode[] => {
    const visibleNodes: TreeNode[] = [node];
    const newPath = [...currentPath, node.user_uid];

    // If this node is expanded, we need to determine what to show
    if (expandedNodes.has(node.user_uid)) {
      // Show children, but hide siblings if any child is expanded
      const expandedChildren = node.children.filter(child => expandedNodes.has(child.user_uid));

      if (expandedChildren.length > 0) {
        // If any child is expanded, show only the expanded children and their descendants
        expandedChildren.forEach(expandedChild => {
          visibleNodes.push(...getVisibleNodes(expandedChild, newPath));
        });
      } else {
        // No children are expanded, show all children
        node.children.forEach(child => {
          visibleNodes.push(...getVisibleNodes(child, newPath));
        });
      }
    }

    return visibleNodes;
  };

  // Get all visible nodes starting from root
  const visibleNodesList = getVisibleNodes(rootNode);

  // Create a map for quick lookup
  const visibleNodesMap = new Map(visibleNodesList.map(node => [node.user_uid, node]));

  // Build the visual hierarchy based on visible nodes
  const buildHierarchyLevel = (
    levelNodes: TreeNode[],
    currentY: number,
    parentId?: string
  ) => {
    const { maxNodesPerRow, horizontalSpacing, verticalSpacing } = getResponsiveConfig();

    // If we have maxNodesPerRow or fewer nodes, arrange them horizontally as before
    if (levelNodes.length <= maxNodesPerRow) {
      const startX = -(levelNodes.length - 1) * horizontalSpacing / 2;

      levelNodes.forEach((node, index) => {
        const nodeX = startX + index * horizontalSpacing;

        // Add the node
        nodes.push({
          id: node.user_uid,
          position: { x: nodeX, y: currentY },
          data: { ...node },
          type: "customNode",
        });

        // Add edge from parent if exists
        if (parentId) {
          edges.push({
            id: `${parentId}-${node.user_uid}`,
            source: parentId,
            target: node.user_uid,
            animated: true,
          });
        }
      });
    } else {
      // More than maxNodesPerRow nodes - arrange in rows
      const numRows = Math.ceil(levelNodes.length / maxNodesPerRow);

      levelNodes.forEach((node, index) => {
        const row = Math.floor(index / maxNodesPerRow);
        const positionInRow = index % maxNodesPerRow;
        const nodesInCurrentRow = Math.min(maxNodesPerRow, levelNodes.length - row * maxNodesPerRow);

        // Calculate position
        const rowY = currentY + (row * verticalSpacing);
        const startX = -(nodesInCurrentRow - 1) * horizontalSpacing / 2;
        const nodeX = startX + positionInRow * horizontalSpacing;

        // Add the node
        nodes.push({
          id: node.user_uid,
          position: { x: nodeX, y: rowY },
          data: { ...node },
          type: "customNode",
        });

        // Add edge from parent if exists
        if (parentId) {
          edges.push({
            id: `${parentId}-${node.user_uid}`,
            source: parentId,
            target: node.user_uid,
            animated: true,
          });
        }
      });
    }

    // Process children for each node in this level
    levelNodes.forEach((node) => {
      if (expandedNodes.has(node.user_uid)) {
        const visibleChildren = node.children.filter(child =>
          visibleNodesMap.has(child.user_uid)
        );

        if (visibleChildren.length > 0) {
          // Check if we should show all children or focus on expanded ones
          const expandedChildren = visibleChildren.filter(child =>
            expandedNodes.has(child.user_uid)
          );

          const childrenToShow = expandedChildren.length > 0 ? expandedChildren : visibleChildren;

          // Calculate the Y position for children
          // If current level has multiple rows, we need to account for that
          const currentLevelRows = Math.ceil(levelNodes.length / maxNodesPerRow);
          const childrenY = currentY + (currentLevelRows > 1 ? (currentLevelRows - 1) * verticalSpacing : 0) + 280;

          buildHierarchyLevel(childrenToShow, childrenY, node.user_uid);
        }
      }
    });
  };

  buildHierarchyLevel([rootNode], 0);

  return { nodes, edges };
};

const generatePeerGraph = (
  horizontalMetrics: any[],
  openModal: (data: any) => void
): { nodes: Node[]; edges: Edge[] } => {
  const startX = 0;
  const startY = 0;
  const spacingY = 180;

  const nodes: Node[] = horizontalMetrics.map((peer, index) => ({
    id: peer.user_uid,
    position: {
      x: startX,
      y: startY + index * spacingY
    },
    data: {
      user_uid: peer.user_uid,
      name: peer.user_name || "Unknown",
      job_title: "Same-level Peer",
      data: peer.data,
      children: [],
    },
    type: "customNode",
  }));

  const edges: Edge[] = [];
  return { nodes, edges };
};


const HierarchyComponent: React.FC = () => {
  useCheckSessionHeartBeat();
  const [hierarchyData, setHierarchyData] = useState<TreeNode | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasAutoFittedOnce = useRef(false);
  const [sessionUid, setSessionUid] = useState("");
  const [userUid, setUserUid] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });

  const [viewMode, setViewMode] = useState<'reportees' | 'peers'>('reportees');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNodeData, setSelectedNodeData] = useState<any>(null);
  const [userImages, setUserImages] = useState<Record<string, string>>({});
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);


  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [highlightedNode, setHighlightedNode] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const [savedViewport, setSavedViewport] = useState<{ x: number, y: number, zoom: number } | null>(null);
  const [shouldPreventAutoFit, setShouldPreventAutoFit] = useState(false);
  const [showNoResultsAlert, setShowNoResultsAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [gridColor, setGridColor] = useState("#e0e0e0");


  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const openModal = (data: any) => {
    // Save current viewport before opening modal
    const reactFlowInstance = document.querySelector('.react-flow');
    if (reactFlowInstance) {
      setShouldPreventAutoFit(true);
    }
    setSelectedNodeData(data);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    // Don't reset shouldPreventAutoFit here - let it remain true to prevent auto-fitting
  };
  const resetViewport = () => {
    setShouldPreventAutoFit(false);
    setUserHasInteracted(false);
    setSavedViewport(null);
  };
  const handleViewModeChange = (mode: 'reportees' | 'peers') => {
    setViewMode(mode);

    // Clear search states when switching modes
    clearSearchStates();

    // Reset viewport behavior when switching modes
    setShouldPreventAutoFit(false);
    setUserHasInteracted(false);
    setSavedViewport(null);
  };
  const findNodeById = (nodeId: string, node: TreeNode): TreeNode | null => {
    if (node.user_uid === nodeId) return node;
    for (const child of node.children) {
      const found = findNodeById(nodeId, child);
      if (found) return found;
    }
    return null;
  };

  const shouldAutoFitRef = useRef(false);

  const toggleNode = useCallback((nodeId: string) => {
    if (!hierarchyData) return;

    setExpandedNodes((prev) => {
      const newExpanded = new Set(prev);
      const wasExpanded = newExpanded.has(nodeId);

      if (wasExpanded) {
        const removeDescendants = (node: TreeNode) => {
          newExpanded.delete(node.user_uid);
          node.children.forEach(removeDescendants);
        };

        const targetNode = findNodeById(nodeId, hierarchyData);
        if (targetNode) {
          removeDescendants(targetNode);
        }
      } else {
        newExpanded.add(nodeId);

        const expandedNode = findNodeById(nodeId, hierarchyData);
        const childNodes = expandedNode?.children || [];
        const uidsToFetch = childNodes
          .map((child: any) => child.user_uid)
          .filter((uid: string | undefined) => !!uid);

        fetchUserImages(uidsToFetch);

        shouldAutoFitRef.current = true;
      }

      setTimeout(() => {
        setShouldPreventAutoFit(false);
        setUserHasInteracted(false);
        setSavedViewport(null);
      }, 100);

      return newExpanded;
    });
  }, [hierarchyData]);

  useEffect(() => {
    const updateGridColor = () => {
      const color = getComputedStyle(document.documentElement)
        .getPropertyValue("--line-color")
        .trim();
      if (color) setGridColor(color);
    };

    // Initial call
    updateGridColor();

    const observer = new MutationObserver(updateGridColor);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style", "data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  const performSearch = useCallback((term: string) => {
    if (!term.trim() || !hierarchyData) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      setShowNoResultsAlert(false);
      return;
    }

    const results: SearchResult[] = [];
    const searchLower = term.toLowerCase();

    if (viewMode === 'reportees') {
      const allNodes = collectAllNodes(hierarchyData);
      allNodes.forEach(node => {
        if (
          node.name.toLowerCase().includes(searchLower) ||
          node.job_title.toLowerCase().includes(searchLower) ||
          node.department.toLowerCase().includes(searchLower)
        ) {
          results.push({
            user_uid: node.user_uid,
            name: node.name,
            job_title: node.job_title,
            department: node.department,
            type: 'reportee'
          });
        }
      });
    } else {
      hierarchyData.horizontal_metrics?.forEach(peer => {
        const peerName = peer.user_name || "Unknown";
        if (
          peerName.toLowerCase().includes(searchLower) ||
          peer.department?.toLowerCase().includes(searchLower)
        ) {
          results.push({
            user_uid: peer.user_uid,
            name: peerName,
            job_title: "Same-level Peer",
            department: peer.department || "",
            type: 'peer'
          });
        }
      });
    }

    setSearchResults(results);
    setShowSearchDropdown(results.length > 0);

    // Hide alert when there are results or when search is cleared
    if (results.length > 0 || !term.trim()) {
      setShowNoResultsAlert(false);
    }
  }, [hierarchyData, viewMode]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    performSearch(value);

    // Hide alert when user is typing
    if (showNoResultsAlert) {
      setShowNoResultsAlert(false);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      if (searchTerm.trim()) {
        if (searchResults.length === 0) {
          // Show alert for no results
          const entityType = viewMode === 'reportees' ? 'employee' : 'peer';
          setAlertMessage(`No ${entityType} found matching "${searchTerm}"`);
          setShowNoResultsAlert(true);

          // Auto-hide alert after 3 seconds
          setTimeout(() => {
            setShowNoResultsAlert(false);
          }, 3000);
        } else if (searchResults.length === 1) {
          // Auto-select if only one result
          handleSearchResultClick(searchResults[0]);
        }
      }
    }
  };

  const clearSearch = () => {
    clearSearchStates();
  };
  const clearSearchStates = () => {
    setSearchTerm("");
    setSearchResults([]);
    setShowSearchDropdown(false);
    setHighlightedNode(null);
    setShowNoResultsAlert(false);
    setNavigateToNodeId(null);
  };
  // Navigate to node function
  const NavigateToNode: React.FC<{ nodeId: string }> = ({ nodeId }) => {
    const { getNode, fitView } = useReactFlow();

    useEffect(() => {
      const timer = setTimeout(() => {
        const targetNode = getNode(nodeId);
        if (targetNode) {
          setHighlightedNode(nodeId);

          fitView({
            nodes: [targetNode],
            duration: 1000,
            padding: 0.3,
            maxZoom: 1.2,
          });

          setTimeout(() => {
            setHighlightedNode(null);
            // Reset navigation after search completes
            setNavigateToNodeId(null);
          }, 4000);
        }
      }, 300);

      return () => clearTimeout(timer);
    }, []);

    return null;
  };

  const [navigateToNodeId, setNavigateToNodeId] = useState<string | null>(null);

  const handleSearchResultClick = (result: SearchResult) => {
    if (viewMode === 'reportees') {
      setUserHasInteracted(false);
      const pathToNode = findPathToNode(result.user_uid, hierarchyData!);
      if (pathToNode && pathToNode.length > 1) {
        const newExpanded = new Set<string>();
        for (let i = 0; i < pathToNode.length - 1; i++) {
          const currentNodeId = pathToNode[i];
          const nextNodeId = pathToNode[i + 1];
          newExpanded.add(currentNodeId);
          const currentNode = findNodeById(currentNodeId, hierarchyData!);
          if (currentNode) {
            const parent = i === 0 ? null : findNodeById(pathToNode[i - 1], hierarchyData!);
            const siblings = parent ? parent.children : [hierarchyData!];
            siblings.forEach(sibling => {
              if (sibling.user_uid !== currentNodeId) {
                const collapseDescendants = (node: TreeNode) => {
                  newExpanded.delete(node.user_uid);
                  node.children.forEach(collapseDescendants);
                };
                collapseDescendants(sibling);
              }
            });
          }
        }
        setExpandedNodes(newExpanded);

        // Add a small delay before fetching images to ensure state is updated
        // Add a small delay before fetching images to ensure state is updated
        setTimeout(() => {
          const nodesToFetch: string[] = [];
          pathToNode.forEach(nodeId => {
            const node = findNodeById(nodeId, hierarchyData!);
            if (node) {
              nodesToFetch.push(nodeId);
              if (newExpanded.has(nodeId)) {
                node.children.forEach(child => {
                  nodesToFetch.push(child.user_uid);
                });
              }
            }
          });

          // Remove duplicates before fetching
          const uniqueNodeIds = Array.from(new Set(nodesToFetch));

          if (uniqueNodeIds.length > 0) {
            fetchUserImages(uniqueNodeIds);
          }
        }, 100);
      }
    }

    setNavigateToNodeId(result.user_uid);
    setShowSearchDropdown(false);
    setSearchTerm(result.name);
  };
  // Reset navigation when mode changes
  useEffect(() => {
    setNavigateToNodeId(null);
  }, [viewMode]);

  useEffect(() => {
    const sid = localStorage.getItem("session_uid") ?? "";
    const uid = localStorage.getItem("user_uid") ?? "";

    if (sid && uid) {
      setSessionUid(sid);
      setUserUid(uid);

      // Clear search states when date changes
      clearSearchStates();

      const selectedDateMoment = moment(selectedDate);
      const currentTime = moment();

      const fromDatetime = selectedDateMoment
        .clone()
        .startOf("day")
        .format("YYYY-MM-DDTHH:mm:ss");

      const toDatetime = selectedDateMoment
        .clone()
        .set({
          hour: currentTime.hour(),
          minute: currentTime.minute(),
          second: currentTime.second(),
        })
        .format("YYYY-MM-DDTHH:mm:ss");

      fetchHierarchyData(
        sid,
        uid,
        fromDatetime,
        toDatetime,
        setHierarchyData,
        setLoading,
        setError
      );
    } else {
      setLoading(false);
      setError("Session not found. Please refresh the page.");
    }
  }, [selectedDate]);

  const fetchUserImages = async (userUids: string[]) => {
    // Filter out any invalid UIDs and UIDs we already have
    const validUids = userUids.filter((uid) => uid && typeof uid === 'string' && uid.trim() !== '');
    const uidsToFetch = validUids.filter((uid) => !userImages[uid]);

    if (uidsToFetch.length === 0) return;

    // console.log('Fetching images for UIDs:', uidsToFetch); // Debug log

    try {
      const queryString = qs.stringify(
        {
          icon_img: true,
          target_user_uid_list: uidsToFetch,
        },
        { arrayFormat: "repeat" }
      );

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/user-profile-images?${queryString}`,
        {
          headers: {
            "user-uid": localStorage.getItem("user_uid") || "",
          },
          responseType: "json",
          withCredentials: true,
        }
      );

      // console.log('Image API response:', response.data); // Debug log

      const blankGif = "R0lGODlhAQABAIABAAAAAP///yH5BAEAAAEALAAAAAABAAEAAAICRAEAOw==";
      const imageMap: Record<string, string> = {};

      response.data?.data?.forEach((entry: any) => {
        if (entry.user_uid && entry.image && entry.image !== blankGif) {
          imageMap[entry.user_uid] = entry.image;
        }
      });

      setUserImages((prev) => ({ ...prev, ...imageMap }));
    } catch (error) {
      console.warn("Failed to fetch user images", error);
      console.error("Image fetch error details:", error);
    }
  };

  const fetchHierarchyData = async (
    sid: string,
    uid: string,
    fromDatetime: string,
    toDatetime: string,
    setHierarchyData: (data: TreeNode) => void,
    setLoading: (loading: boolean) => void,
    setError: (error: string | null) => void
  ) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(API_URL_HIERARCHY, {
        params: {
          from_datetime: fromDatetime,
          to_datetime: toDatetime,
        },
        headers: {
          "user-uid": uid,
          accept: "application/json",
        },
        withCredentials: true,
      });

      if (!response || !response.data) {
        openNotification({
          type: "error",
          message: "No response received from server.",
        });
        return;
      }

      const { status, data, message } = response.data;

      if (status === "Success" && data) {
        const extractedData = extractTreeData(data);
        setHierarchyData(extractedData);
      } else if (data?.toLowerCase?.() === "access denied") {
        setError("Access Denied");
      } else if (message?.toLowerCase?.().includes("invalid session")) {
        setError("Session is invalid. Please refresh the page.");
      } else {
        openNotification({
          type: "error",
          message: message || "Could not load hierarchy data. Please try again later.",
        });
      }
    } catch (error: any) {
      const responseData = error?.response?.data;
      const errMessage = responseData?.message?.toLowerCase?.() || "";
      const errData = responseData?.data?.toLowerCase?.() || "";

      if (errData === "access denied") {
        setError("Access Denied");
      } else if (errMessage.includes("invalid session")) {
        setError("Session is invalid. Please refresh the page.");
      } else {
        openNotification({
          type: "error",
          message: responseData?.message || "Something went wrong while fetching hierarchy data.",
        });
      }
    } finally {
      setLoading(false);
      hasAutoFittedOnce.current = false;
    }
  };

  useEffect(() => {
    if (hierarchyData?.user_uid) {
      fetchUserImages([hierarchyData.user_uid]);
    }
  }, [hierarchyData]);

  useEffect(() => {
    if (hierarchyData) {
      // Only clear if we have a search term (to avoid clearing on initial load)
      if (searchTerm.trim()) {
        clearSearchStates();
      }
    }
  }, [hierarchyData]);

  useEffect(() => {
    if (viewMode === "peers" && (hierarchyData?.horizontal_metrics?.length ?? 0) > 0) {
      const peerUids = (hierarchyData?.horizontal_metrics ?? [])
        .map((peer: any) => peer.user_uid)
        .filter((uid: string) => !!uid);
      fetchUserImages(peerUids);
    }
  }, [viewMode, hierarchyData]);

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (searchInputRef.current && target && !searchInputRef.current.contains(target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  let nodes: Node[] = [];
  let edges: Edge[] = [];

  if (hierarchyData) {
    if (viewMode === "reportees") {
      const graph = generateGraph(hierarchyData, expandedNodes, toggleNode);
      nodes = graph.nodes;
      edges = graph.edges;
    } else if (viewMode === "peers") {
      const graph = generatePeerGraph(hierarchyData.horizontal_metrics || [], openModal);
      nodes = graph.nodes;
      edges = graph.edges;
    }
  }

  const getNodeLevel = (nodeId: string, node: TreeNode, level = 0): number | null => {
    if (node.user_uid === nodeId) return level;
    for (const child of node.children) {
      const found = getNodeLevel(nodeId, child, level + 1);
      if (found !== null) return found;
    }
    return null;
  };

  const AutoFitView: React.FC = () => {
    const reactFlowInstance = useReactFlow();
    const { fitView, getNodes, setViewport } = reactFlowInstance || {};

    // Only disable auto-fitting for search when user hasn't interacted yet
    // But allow it to be disabled for other auto-fit scenarios
    const isSearchDisabled = !!navigateToNodeId && !userHasInteracted;

    useEffect(() => {
      if (isSearchDisabled || !getNodes || hasAutoFittedOnce.current) return;

      const allNodes = getNodes();
      if (allNodes.length === 0) return;

      // Initial fit view for first load
      if (fitView) {
        fitView({ padding: 0.3 });
      }
      setTimeout(() => {
        if (setViewport) {
          setViewport({ x: 400, y: 50, zoom: 1 });
        }
        hasAutoFittedOnce.current = true;
      }, 1);
    }, [isSearchDisabled, getNodes, fitView, setViewport, nodes.length]);

    useEffect(() => {
      // This is for general auto-fitting (expand/collapse, resize, etc.)
      // Don't auto-fit if user has manually interacted AND it's not a search
      if (!fitView || !hasAutoFittedOnce.current || (shouldPreventAutoFit && !navigateToNodeId)) return;

      // Small delay to ensure DOM has updated after focus mode change
      const timer = setTimeout(() => {
        const allNodes = getNodes?.();
        if (allNodes && allNodes.length > 0) {
          fitView({
            padding: 0.3,
            duration: 600,
            maxZoom: 1.2
          });
        }
      }, 150);

      return () => clearTimeout(timer);
    }, [nodes.length, expandedNodes.size, windowWidth, fitView, getNodes, shouldPreventAutoFit, navigateToNodeId]);

    return null;
  };

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          marginTop: "20%",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1.3rem",
          textAlign: "center",
          padding: "0 20px",
          fontWeight: 500,
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <div className="hierarchy-container">
      <div className="hierarchyTitleContainer">
        <h6 className="hierarchy-title">My Organization View</h6>
      </div>

      <div className="hierarchy-controls-wrapper">
        <div className="toggle-buttons">
          <button
            className={`toggle-btn ${viewMode === "reportees" ? "active" : ""}`}
            onClick={() => handleViewModeChange("reportees")}
          >
            Reportees
          </button>
          <button
            className={`toggle-btn ${viewMode === "peers" ? "active" : ""}`}
            onClick={() => handleViewModeChange("peers")}
          >
            Peers
          </button>
        </div>

        {/* Search Bar */}
        <div className="Hsearch-container" ref={searchInputRef}>
          <div className="search-input-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder={`Search ${viewMode === 'reportees' ? 'employees' : 'peers'}...`}
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              className="MyOrg-search-input"
            />
            {searchTerm && (
              <FaTimes className="search-clear" onClick={clearSearch} />
            )}
          </div>

          {/* No Results Alert */}
          {showNoResultsAlert && (
            <div className="search-alert">
              <span className="alert-icon">⚠️</span>
              <span className="alert-text">{alertMessage}</span>
            </div>
          )}

          {showSearchDropdown && searchResults.length > 0 && (
            <div className="search-dropdown">
              {searchResults.map((result, index) => (
                <div
                  key={`${result.user_uid}-${index}`}
                  className="search-result-item"
                  onClick={() => handleSearchResultClick(result)}
                >
                  <div className="search-result-name">{result.name}</div>
                  <div className="search-result-details">
                    {result.job_title} • {result.department}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Date Picker */}
        <div className="flatpickr-wrapper">
          <Flatpickr
            value={selectedDate}
            options={{
              dateFormat: "Y-m-d",
              maxDate: "today",
            }}
            onChange={(date: Date[]) => {
              if (date[0]) setSelectedDate(date[0]);
            }}
            className="flatpickr-input"
            placeholder="Select date"
          />
          <Calendar className="calendar-icon" size={16} />
        </div>
      </div>

      {/* Main Hierarchy View */}
      <div className="hierarchy-wrapper">
        {loading ? (
          <SkeletonLoader type="hierarchy" />
        ) : (
          <ReactFlowProvider>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              proOptions={{ hideAttribution: true }}
              nodeTypes={{
                customNode: (props) => (
                  <CustomNode
                    {...props}
                    toggleNode={toggleNode}
                    expanded={expandedNodes.has(props.id)}
                    openModal={openModal}
                    userImages={userImages}
                    highlighted={highlightedNode === props.id}
                  />
                ),
              }}
              zoomOnScroll={false}
              panOnScroll
              panOnDrag={true}
              zoomOnDoubleClick={false}
              onMoveStart={() => {
                setUserHasInteracted(true);
                setShouldPreventAutoFit(true);
              }}
              onMoveEnd={(event, viewport) => {
                setSavedViewport(viewport);
              }}
            >
              <AutoFitView />
              {/* Conditionally render NavigateToNode. 
                  - `MapsToNodeId` triggers the navigation.
                  - `!userHasInteracted` ensures we stop auto-zooming if the user takes control.
                  - `key` ensures the component re-mounts for a new search, re-triggering the useEffect. */}
              {navigateToNodeId && (
                <NavigateToNode key={navigateToNodeId} nodeId={navigateToNodeId} />
              )}
              <Controls showInteractive={false} className="hierarchy-controls" />
              <Background color={gridColor} gap={16} />
              {viewMode === "peers" && nodes.length === 0 && (
                <div className="no-peers-message">
                  <p>You don't have any peers to show here.</p>
                </div>
              )}
            </ReactFlow>
          </ReactFlowProvider>
        )}
      </div>

      <NodeDetailsModal
        isOpen={modalOpen}
        onClose={closeModal}
        data={selectedNodeData}
        userImages={userImages}
      />
    </div>
  );
};

export default HierarchyComponent;
